import React from 'react';
import { toast } from 'react-toastify';
import { handleError } from '../../common/utils/util.errors';
import { deepCopy } from '../../common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { useQueryClient } from 'react-query';
import { CCReport } from '../construction-cost-report/construction-cost-report.type';
import QCCReportRenderer from './QCCReportRenderer';
import { QCCReport } from './quarterly-cc-report.type';
import { getQCCReport, updateQCCReport, updateQCCReportComps } from './quarterly-cc-report.api';

interface QCCReportWrapperProps {
  initialReport: CCReport;
}

export const QCCReportWrapper: React.FC<QCCReportWrapperProps> = ({ initialReport }) => {
  const [report, setReport] = React.useState<QCCReport>(deepCopy(initialReport));
  const [isReportDirty, setIsReportDirty] = React.useState(false);
  const [isReportLoading, setIsReportLoading] = React.useState(false);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    setReport(deepCopy(initialReport));
    setIsReportDirty(false);
  }, [initialReport]);

  const updateReport = (updatedReport: QCCReport) => {
    setReport(updatedReport);
    setIsReportDirty(true);
  };

  const resetReport = () => {
    setReport(deepCopy(initialReport));
    setIsReportDirty(false);
  };

  const saveReport = async () => {
    try {
      setIsReportLoading(true);
      await updateQCCReport(report.id, report);
      await updateQCCReportComps(report.id, report.comparableProjects ?? []);
      toast.success('All Changes Saved');
      setIsReportDirty(false);

      const updatedReport = await getQCCReport(report.id);
      setReport(updatedReport);
      queryClient.invalidateQueries([keys.qccReport, report.id]);
    } catch (error) {
      handleError(error);
    } finally {
      setIsReportLoading(false);
    }
  };

  const refetchReport = async () => {
    try {
      setIsReportLoading(true);
      const updatedReport = await getQCCReport(report.id);
      setReport(updatedReport);
      setIsReportDirty(false);
      await queryClient.invalidateQueries([keys.qccReport, report.id]);
    } catch (error) {
      handleError(error);
    } finally {
      setIsReportLoading(false);
    }
  };

  return (
    <QCCReportRenderer
      report={report}
      updateReport={updateReport}
      isReportDirty={isReportDirty}
      isReportLoading={isReportLoading}
      saveReport={saveReport}
      undoChanges={resetReport}
      refetchReport={refetchReport}
    />
  );
};
